/**
 * Shared UI class recipes — single source of truth for the repeated Tailwind
 * combos that were hand-duplicated across dozens of call sites. Colors go through
 * the design-system tokens where one exists (warm-brown, bg-card, border-*,
 * text-text-*); the `synthetic` accent uses raw purple-600/500 until a `synthetic`
 * token is added.
 *
 * Use with `className={btnPrimary}` etc.; append extras with a template string
 * where a site genuinely needs them (e.g. `className={`${btnPrimary} w-full`}`).
 */

// Filled action button — the modular filled button where COLOR IS A CHOICE, not
// baked in. Same geometry + elevation across tones so only the HUE varies by
// intent: brand=warm-brown (the default primary), info=blue, success=green,
// warning=amber, error=red, synthetic=purple (the AI/synthetic-generation accent).
// Filled action-button recipe, parameterized by tone. `btnFilled(tone)` supplies the
// full button geometry; `filledFill(tone)` is the color-only fragment (bg+hover+white)
// for custom-geometry buttons that btnFilled's geometry does not fit.
const FILLED_TONE = {
  brand: 'bg-warm-brown hover:bg-warm-brown-hover',
  info: 'bg-info hover:bg-info/90',
  success: 'bg-success hover:bg-success/90',
  warning: 'bg-warning hover:bg-warning/90',
  error: 'bg-error hover:bg-error/90',
  synthetic: 'bg-purple-600 hover:bg-purple-500',
} as const;
export type FilledTone = keyof typeof FILLED_TONE;

// Color-only fill fragment (bg + hover + white text) for a filled button at `tone`,
// the custom-geometry counterpart to the full-geometry btnFilled recipe.
export function filledFill(tone: FilledTone = 'brand') {
  return `${FILLED_TONE[tone]} text-white`;
}

// Module-private full-geometry filled-button recipe; the only public entry point is
// btnPrimary (btnFilled('brand')). Toned call sites use the color-only filledFill() above.
function btnFilled(tone: FilledTone = 'brand') {
  return `inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-card-sm ${filledFill(tone)}`;
}

// Primary brand action button (Save / Create / Continue / Copy / submit) — btnFilled
// at the brand tone. Named separately since it's the overwhelmingly common case.
export const btnPrimary = btnFilled('brand');

// Brand fill color fragment — the warm-brown+white+hover triad, for custom-geometry
// brand buttons that btnFilled/btnPrimary geometry does not fit (compact/rounded-full).
export const brandFill = filledFill('brand');

// Secondary bordered button (muted text, hover cream) — the neutral counterpart
// to btnPrimary used for Copy / Cancel / secondary toolbar actions.
export const btnSecondary =
  'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-text-secondary border border-border-subtle rounded-lg hover:bg-cream-100 hover:text-text-primary transition-colors';

// Neutral FILLED button surface — always-cream fill (not hover-only like
// btnSecondary, not a brand/status hue like btnFilled). For 'Run again' /
// 'Regenerate' / copy utility buttons. Surface+hover only; append geometry
// (rounded-full/rounded-lg, px/py, border) per site. Cream is the app's neutral
// fill language (chips/segments); the old bg-card/90 spelling converges here.
export const btnNeutral = 'bg-cream-100 hover:bg-cream-100/80 transition-colors';

// Medium secondary button — the px-4 py-2 / text-sm tier for modal Cancel and
// prominent secondary actions (btnSecondary is the compact toolbar tier).
export const btnSecondaryMd =
  'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-text-primary border border-border-subtle rounded-lg hover:bg-cream-100 transition-colors';

// Small lowercase brand button used in Settings / API-keys.
export const btnPrimarySm =
  'inline-flex items-center gap-1.5 px-4 py-2 bg-warm-brown text-white text-xs font-medium rounded-md hover:bg-warm-brown-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors lowercase';

// OAuth / social sign-in pill button on the auth pages (login / signup) — the
// bordered card-surface pill sibling to authSubmit.
export const authOAuthButton =
  'w-full flex items-center justify-center gap-2.5 px-5 py-3 border border-border-subtle text-sm font-medium text-text-primary bg-card rounded-full transition-all duration-200 hover:bg-cream-50 hover:border-border-focus hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed animate-fade-in stagger-1';

// Auth marketing panel headline + subcopy (dark branding side of the 4 auth pages).
export const authMarketingHeadline = 'text-4xl font-light text-white leading-tight tracking-tight';
export const authMarketingSubtext = 'text-sm text-white/80 mt-6';

// Auth form-side page heading (Welcome back / Create your account / Reset password).
export const authHeading = 'text-3xl font-light text-text-primary mb-2 tracking-tight';

// Secondary/confirmation auth heading — the text-2xl light sibling of authHeading
// (Check your email / Reset password / status screens). Append mb-* per site.
export const authHeadingSm = 'text-2xl font-light text-text-primary tracking-tight';

// Onboarding step h1 — the responsive text-3xl/4xl light hero heading on each step.
export const onboardingHeading =
  'text-3xl sm:text-4xl font-light text-text-primary tracking-tight mb-3';

// Tone tag pill — a small uppercase tinted status-surface pill (Suggested /
// Negative / Positive). Distinct from StatusBadge (run statuses); this tags a
// type/state. Append `flex items-center gap-1.5` at icon-bearing call sites.
export function toneTag(tone: 'info' | 'error' | 'success' | 'warning') {
  return `text-tiny font-bold px-1.5 py-0.5 rounded bg-${tone}-surface text-${tone} border border-${tone}-edge uppercase tracking-wider`;
}

// Tone callout / inline-alert box — tinted surface + edge + text for a form error /
// load-failure strip / notice. Color only; append radius + padding + text-size per site.
export function toneCallout(tone: 'info' | 'error' | 'success' | 'warning') {
  return `bg-${tone}-surface border border-${tone}-edge text-${tone}`;
}

// Soft-fill tone action button — tinted counterpart to solid btnFilled(tone)
// (delete=error, approve=success, run=info): surface fill + tone text + edge,
// darkening to surface-strong on hover. Append geometry (px/py/rounded/text) per site.
export function btnSoft(tone: 'info' | 'error' | 'success' | 'warning') {
  return `bg-${tone}-surface text-${tone} border border-${tone}-edge hover:bg-${tone}-surface-strong transition-colors`;
}

// Full-width pill submit button on the auth pages (login / signup / …).
export const authSubmit =
  'w-full py-3 px-5 bg-warm-brown text-white text-sm font-medium rounded-full transition-all duration-200 hover:bg-warm-brown-hover hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed';

// Outline auth pill — the bordered secondary sibling of authSubmit (Try another
// email / Back to login). Append block/text-center for the Link variant.
export const authSubmitOutline =
  'w-full py-3 px-5 border border-border-subtle text-text-primary text-sm font-medium rounded-full transition-all duration-200 hover:bg-cream-100 hover:border-border-focus';

// Standard text input / textarea field.
export const inputBase =
  'w-full px-3 py-2.5 text-sm bg-card border border-border-default rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-border-focus focus:shadow-[var(--shadow-focus)] transition-colors';

// Compact input — the px-2.5 py-1.5 / rounded-md / 13px variant used heavily in
// the experiments filter rows. Same tokens as inputBase, tighter geometry.
export const inputBaseSm =
  'w-full px-2.5 py-1.5 text-body-sm bg-card border border-border-default rounded-md text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-border-focus focus:shadow-[var(--shadow-focus)] transition-colors';

// <select> field — shares the inputBase surface/focus so selects and text inputs
// match. Append `appearance-none` + a chevron at the call site if needed.
export const selectBase =
  'w-full px-3 py-2.5 text-sm bg-card border border-border-default rounded-lg text-text-primary focus:outline-none focus:border-border-focus focus:shadow-[var(--shadow-focus)] transition-colors';

// Content card / panel surface — the repeated bg-card + subtle border + rounded-xl
// + card shadow. Append padding/layout (p-6, overflow-hidden, hover:*) per site.
export const card = 'bg-card border border-border-subtle rounded-xl shadow-card-sm';

// File-dropzone active (drag-over) tint — warm-brown border + faint brand fill.
export const dropzoneActive = 'border-warm-brown bg-warm-brown/5';

// FLAT card/container — same surface as `card` but no elevation shadow. For nested
// panels and table wrappers that sit inside a page and shouldn't float.
export const cardFlat = 'bg-card border border-border-subtle rounded-xl';

// Score → quality tier. SINGLE source for the score-band boundaries used by every
// score badge/dial (metric-analysis-sidebar, sessions-table, the session detail page).
// Callers map the tier to their own token set (plain text / -surface / -surface-strong),
// but the 80/60 boundaries live here so a score never lands in a different band on
// different surfaces (the session detail page previously used a divergent 50 warn boundary).
export type ScoreTier = 'good' | 'warn' | 'bad';
export function scoreTier(score: number): ScoreTier {
  return score >= 80 ? 'good' : score >= 60 ? 'warn' : 'bad';
}

// Progress-bar track — the neutral cream rail that clips a colored fill. The inner
// fill (bg-info, or a per-tier color mapped via scoreTier above) stays per-site
// since it is semantic/data.
export const progressTrack = 'h-1 bg-cream-200 rounded-full overflow-hidden';

// Borderless elevated card — bg-card + rounded-xl + card shadow, NO border, clips
// overflow (table/list wrappers). Sibling of card (which has the border).
export const tableCard = 'bg-card rounded-xl overflow-hidden shadow-card-sm';

// Floating dropdown/popover menu panel surface (heavier shadow than a card).
// Append positioning (absolute/right-0/top-full/mt-1), width, max-h, z, py-1.
export const dropdownPanel = 'bg-card border border-border-subtle rounded-xl shadow-card-lg';

// Recharts custom-tooltip surface — compact elevated card (smaller radius/padding
// than the page-scale card/dropdownPanel). Append backdrop-blur-md where wanted. Single source of
// truth for the tooltip look, shared by every chart's custom-content tooltip (unified-chart,
// trend-line-chart, area-sparkline) so there is no hand-synced CSSProperties copy to drift.
export const chartTooltip =
  'bg-card border border-border-subtle rounded-md px-2.5 py-1.5 shadow-card-lg';

// Right-edge slide-in drawer / detail panel surface (left border + heavy shadow +
// column flex). Append positioning (fixed/absolute, top-0 right-0 h-full), width,
// z-index, and the slide transition per site — those legitimately vary.
export const drawerPanel = 'bg-card border-l border-border-subtle shadow-card-lg flex flex-col';

// --- Page layout scaffolding (one spelling per structural slot) ---

// Full-height page shell wrapper (canvas bg, column, clips overflow).
export const pageShell = 'flex-1 flex flex-col h-full bg-canvas overflow-hidden';

// Scrollable page-content pane — grow, scroll-y, p-6 (the main dashboard body).
export const scrollBody = 'flex-1 overflow-y-auto p-6';

// Page top bar / header row (title on the left, actions on the right).
export const topBar =
  'h-14 flex items-center justify-between px-6 border-b border-border-subtle bg-canvas';

// Left-aligned page header band — h-14 with a single title (no right-side actions,
// so no justify-between/bg-canvas like topBar).
export const headerBand = 'h-14 flex items-center px-6 border-b border-border-subtle';

// Detail-panel header row — the shorter h-12 sibling of topBar used inside cards,
// drawers, and sub-pages (card bg rather than canvas).
export const panelHeader =
  'h-12 flex items-center justify-between px-6 border-b border-border-subtle bg-card';

// Sub-toolbar meta band under panelHeader (experiment-detail meta+pills row).
export const subToolbar =
  'h-11 flex items-center justify-between px-6 border-b border-border-subtle bg-card';

// Toolbar row — h-12, px-5, no background. The transparent sibling of panelHeader
// used for sub-toolbars sitting directly on the page canvas.
export const toolbarRow =
  'h-12 flex items-center justify-between px-5 border-b border-border-subtle';

// Vertical hairline divider between toolbar/meta items (1px rule). h-3 sibling for shorter runs.
export const vDivider = 'w-px h-4 bg-border-subtle';

// Card/section header band — the px-6 py-4 title+action row at the top of a card
// (height driven by content rather than a fixed h-12/h-14).
export const cardHeader =
  'flex items-center justify-between px-6 py-4 border-b border-border-subtle';

// Card FOOTER band — the bottom action row of a card (top border + faint cream).
export const cardFooter = 'px-5 py-4 border-t border-border-subtle bg-cream-50';

// Custom dropdown/select TRIGGER surface — the bg-card bordered button that opens
// an inline menu (matches <FilterDropdown>'s trigger). Append geometry (px/py,
// w-full justify-between) per site.
export const selectTrigger =
  'bg-card border border-border-default rounded-lg text-text-primary hover:bg-cream-50 transition-colors shadow-card-xs';

// Neutral OUTLINED button on a card surface — bg-card fill + subtle border + cream
// hover (distinct from transparent btnSecondary and cream-filled btnNeutral).
export const btnOutline =
  'bg-card border border-border-subtle rounded-md text-xs font-medium text-text-primary hover:bg-cream-50 transition-colors';

// Border-only hover-shift button — border darkens on hover (no fill change),
// distinct from btnSecondary (cream FILL hover). Onboarding Copy/Refresh/Skip.
// Append text tone + px/py per site.
export const btnBorderHover =
  'flex items-center gap-2 border border-border-subtle hover:border-border-focus transition-all rounded';

// Pager/pagination button — muted text, hover darkens + card fill. Append the
// disabled treatment per site.
export const pagerButton =
  'px-3 py-1.5 text-tiny text-text-secondary hover:text-text-primary hover:bg-card rounded-lg transition-colors';

// Destructive ghost icon button — muted icon that turns error-red + error-surface
// on hover (delete/remove X controls). Color only; append p-1.5/rounded per site.
export const btnGhostDanger =
  'text-text-tertiary hover:text-error hover:bg-error-surface transition-colors';

// Neutral ghost icon button — muted icon that darkens to primary + cream fill on
// hover (copy/expand/inline-row actions). Completes the btnGhost/btnGhostDanger/
// closeBtn family. Geometry (p-1/p-1.5/rounded) stays per call site.
export const btnGhost =
  'text-text-secondary hover:text-text-primary hover:bg-cream-100 transition-colors';

// Bg-less danger hover — muted icon/text that turns error-red on hover, NO fill
// (btnGhostDanger adds the error-surface fill). Append geometry per site.
export const linkDanger = 'text-text-tertiary hover:text-error transition-colors';

// Chip remove / muted-to-primary X control — tertiary icon that darkens on hover.
export const chipRemove = 'text-text-tertiary hover:text-text-primary transition-colors';

// Auth-form field label (Email / Password …) — larger + darker than the uppercase
// fieldLabel; matches the auth pages' distinct type treatment.
export const authFieldLabel = 'block text-sm font-medium text-text-primary mb-1.5';

// Underline tab — the color states for a border-b-2 tab (active = dark text +
// dark underline, inactive = muted text + transparent underline that darkens on
// hover). Geometry (px/py/text size) stays at the call site; combine with a base
// like `border-b-2 transition-colors`. NOT for the pill/fill tab family.
export function tabUnderline(active: boolean) {
  return active
    ? 'text-text-primary border-text-primary'
    : 'text-text-secondary border-transparent hover:text-text-primary';
}

// Toggle switch — track + thumb geometry. The on-track tone (bg-success/…) and the
// thumb translate (translate-x-4/-0) stay at the call site since they're stateful.
export const switchTrack = 'relative w-9 h-5 rounded-full transition-colors';
export const switchThumb =
  'absolute top-0.5 left-0.5 w-4 h-4 bg-card rounded-full transition-transform shadow-card-xs';

// Centered full-screen column used by the auth pages' form side.
export const authColumn = 'min-h-screen bg-canvas flex items-center justify-center px-8';

// Right-hand form column of the split auth layout (heading + form; sibling of AuthMarketingPanel).
export const authFormColumn = 'flex-1 flex flex-col justify-center px-8 md:px-16 lg:px-24';

// Success/confirmation icon circle (green check surface) centered above a message.
export const successCircle =
  'w-16 h-16 rounded-full bg-success-surface border border-success-edge flex items-center justify-center mx-auto mb-6';

// Micro-label — the tiny uppercase section/table-column label used across the app
// (STATUS, AGENT, LAST RUN, …). The de-facto default spelling, named once.
export const microLabel = 'text-tiny font-semibold text-text-secondary uppercase tracking-wider';

// Table header cell — microLabel plus the <th> padding/alignment. One place for
// every table's column-header row.
export const tableHeadCell = `${microLabel} px-4 py-3 text-left`;

// Table header ROW — the <tr> wrapper behind the header cells (bottom border +
// faint cream fill). Sibling to tableHeadCell (which styles the <th>s).
export const tableHeadRow = 'border-b border-border-subtle bg-cream-50';

// Table BODY row — the hoverable <tr>. Append `cursor-pointer` where clickable.
export const tableRow = 'group hover:bg-cream-50 transition-colors';

// Segmented toggle-group container — the cream pill wrapping 2-3 segment buttons.
// Append `w-fit`/`mb-3` layout per site.
export const segmentGroup =
  'inline-flex p-0.5 gap-1 bg-cream-100 border border-border-subtle rounded-lg';

// Pill variant of segmentGroup — rounded-full bg-card shell for auth/onboarding
// toggle groups (vs the default rounded-lg cream data-tool shell).
export const segmentGroupPill = 'rounded-full border border-border-subtle bg-card p-1';

// Segment button color states (sibling to tabUnderline for the fill/pill family).
// Geometry stays at the call site. Variants: 'card' = raised white pill (for a
// cream track), 'cream' = flat cream fill (for a white/canvas track), 'brand' =
// warm-brown fill (auth/snippet pills). Inactive is always muted→primary.
const SEGMENT_ACTIVE = {
  card: 'bg-card text-text-primary shadow-card-xs',
  cream: 'bg-cream-100 text-text-primary',
  brand: 'bg-warm-brown text-white',
} as const;
export function segmentButton(active: boolean, variant: keyof typeof SEGMENT_ACTIVE = 'card') {
  return active ? SEGMENT_ACTIVE[variant] : 'text-text-secondary hover:text-text-primary';
}

// Dropdown menu item color states (for custom inline dropdowns not using
// <FilterDropdown>). Geometry (px/py/text-size/text-left) stays at the call site.
export function dropdownItem(active = false) {
  return active
    ? 'text-text-primary font-medium bg-cream-100'
    : 'text-text-secondary hover:text-text-primary hover:bg-cream-100 transition-colors';
}

// Dashboard page title — the h2 heading at the top of each main page.
export const pageTitle = 'text-h2 font-semibold text-text-primary tracking-tight';

// Section / card / panel title — one tier below pageTitle. The text-base semibold
// heading on every card, dialog, and detail-panel header. Append per-site trailing
// utilities (truncate, mb-*, text-center).
export const sectionTitle = 'text-base font-semibold text-text-primary';

// Subsection/panel heading one tier below sectionTitle (text-sm/medium vs text-base/semibold).
// Only for heading elements; the raw combo is common on non-heading labels too.
export const subsectionTitle = 'text-sm font-medium text-text-primary';

// Uppercase 12px section label — structure only, NO color: append text-text-primary
// for a prominent section heading ("System Alerts", "Live Feed") or text-text-secondary
// for a muted sub-label ("Last 30 days"). One tier up from the 11px microLabel.
export const sectionLabel = 'text-label font-semibold uppercase tracking-wider';

// Mono micro-metadata caption — tiny tertiary monospace text for IDs / counts /
// timestamps shown as metadata.
export const monoMeta = 'text-[10px] text-text-tertiary font-mono';

// Meta bar — the mono tiny-caption row of metadata items (Agent / N variants /
// N cases / Created) under an experiment header.
export const metaBar = 'flex items-center gap-4 text-tiny text-text-secondary font-mono';

// Stat quick-label — the 10px uppercase label under a stat number (Status/
// Duration/Cost). 10px/tracking-wide sibling of the 11px microLabel.
export const statLabel = 'text-[10px] text-text-secondary uppercase tracking-wide mt-1';

// Lowercase form-field label — the settings/experiments lowercase micro-label
// (distinct from the UPPERCASE fieldLabel).
export const lowercaseLabel = 'block text-[10px] font-medium text-text-secondary mb-1 lowercase';

// Sentence-case form label above an input (Name / Input / Slack webhook …) — the
// plain modal/settings label. Distinct from the UPPERCASE fieldLabel, the
// lowercase lowercaseLabel, and the larger auth authFieldLabel.
export const formLabel = 'block text-xs font-medium text-text-secondary mb-1';

// Neutral metadata chip — the cream-filled bordered pill for counts / tags / mono
// metadata (NOT a status pill; those use StatusBadge). Append shape/size
// (rounded-full, px-2, text-[10px]) per site.
export const chip = 'bg-cream-100 border border-border-subtle text-text-secondary';

// Inline code / kbd chip — the chip surface + monospace + default rounded/padding.
// For <code>/<kbd>/slug pills. Append size (text-xs/text-[10px]) per site.
export const codeChip = `${chip} font-mono rounded px-1.5 py-0.5`;

// Read-only code/JSON <pre> block surface (cream-50, subtle border, rounded, mono).
// Append max-h/overflow/hover + text tone per site.
export const codeBlock = 'text-xs bg-cream-50 border border-border-subtle rounded-lg p-4 font-mono';

// Gold text link — the inline accent link (gold, warms on hover). Append size.
export const textLink = 'text-gold hover:text-warm-brown-hover transition-colors';

// Muted text link fragment — secondary text that darkens to primary on hover.
// Color only; append the size (text-sm/text-xs/text-tiny) per site.
export const linkMuted = 'text-text-secondary hover:text-text-primary transition-colors';

// Clickable name/identifier that warms to warm-brown on hover (agent slug, user id).
// Color only; the group-hover variant is handled per site. Sibling of linkMuted.
export const linkWarmHover = 'text-text-primary hover:text-warm-brown transition-colors';

// Full-screen dim/blur overlay backdrop behind a drawer/modal. Append z + any
// transition per site (the shared <Modal> already includes this internally).
export const overlayBackdrop = 'fixed inset-0 bg-overlay backdrop-blur-sm';

// Bordered clipping container — subtle border + rounded-lg + overflow-hidden.
// Prefix with a bg (bg-canvas/bg-cream-100) per site if needed.
export const borderedClip = 'border border-border-subtle rounded-lg overflow-hidden';

// Full-height skeleton/page shell with fade-in (loading placeholders).
export const skeletonShell = 'w-full h-full flex flex-col overflow-hidden animate-fade-in';

// Settings lowercase section heading (text-sm medium, lowercase).
export const settingsHeading = 'text-sm font-medium text-text-primary mb-1 lowercase';

// Uppercase form-field label above an input (block + bottom margin). Same tiny
// uppercase treatment as microLabel; named separately since it's a <label>.
export const fieldLabel =
  'block text-tiny font-semibold text-text-secondary uppercase tracking-wider mb-2';

// Icon/close ("X") button — small padded control that dims + tints on hover.
// One geometry for every dismiss/close control. Append disabled/opacity extras.
export const closeBtn =
  'p-1.5 rounded-lg text-text-tertiary hover:text-text-primary hover:bg-cream-100 transition-colors';

// Auth-page pill input (rounded-full field with a warm-brown focus ring) — the
// deliberately distinct auth aesthetic, sibling to authSubmit. Append per-field
// padding (pl-11 for an icon, etc.) at the call site.
export const authInput =
  'w-full py-3 text-sm bg-card border border-border-subtle text-text-primary rounded-full focus:outline-none focus:ring-4 focus:ring-warm-brown/10 focus:border-warm-brown transition-all duration-300 placeholder:text-text-tertiary';
